import React from 'react';
import { POLICYTYPE, USER_PERMISSION, USER_DAL } from '~/modules/common/enums';
import RoleFacet, { primaryRoleFacet } from '../SearchBox/Facets/RoleFacet';
import LocationFacet from '../SearchBox/Facets/LocationFacet';
import DivisionFacet from '../SearchBox/Facets/DivisionFacet';
import ProgramFacet, {
  EnhancedPMProgramFacet
} from '../SearchBox/Facets/ProgramFacet';
import ClientFacet, {
  EnhancedPMClientFacet
} from '../SearchBox/Facets/ClientFacet';
import ProjectFacet, {
  EnhancedPMProjectFacet
} from '../SearchBox/Facets/ProjectFacet';
import ServiceCenterFacet from '../SearchBox/Facets/ServiceCenterFacet';
import SkillFacet from '../SearchBox/Facets/SkillFacet';
import CostCenterFacet from '../SearchBox/Facets/CostCenterFacet';
import EmployeeTypeFacet from '../SearchBox/Facets/EmployeeTypeFacet';
import DepartmentFacet from '../SearchBox/Facets/DepartmentFacet';
import TagFacet from '../SearchBox/Facets/TagFacet';
import ResourcePoolFacet from '../SearchBox/Facets/ResourcePoolFacet';
import {
  ResourceFacet,
  ProjectResourceFacet
} from '../SearchBox/Facets/UserFacet';

const withPolicyBasedGroupFacets = (
  policy,
  scope
) => BaseComponent => props => (
  <BaseComponent {...props} policy={policy} scope={scope} />
);

const hasResourceManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(USER_DAL.RESOURCE_MANAGER);

const hasResourcePoolManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(USER_DAL.RESOURCE_POOL_MANAGER);

const hasProjectManagerDataAccessLevel = permission =>
  permission.dataAccessLevelUris.includes(USER_DAL.PROJECT_MANAGER);

const applyPolicyToGroupFacet = (policy, facet, scope) => ({
  ...facet,
  DetailsControl: withPolicyBasedGroupFacets(
    policy,
    scope
  )(facet.DetailsControl)
});

const hasViewPermission = ({
  permissions,
  actionUri,
  includeProjectManagerDataAccessScope = false
}) =>
  permissions.some(
    p =>
      p.permissionActionUri === actionUri &&
      (hasResourceManagerDataAccessLevel(p) ||
        hasResourcePoolManagerDataAccessLevel(p) ||
        (includeProjectManagerDataAccessScope &&
          hasProjectManagerDataAccessLevel(p)))
  );

const getFacets = ({
  groupSettings,
  permissions,
  legacyDepartmentDisabled,
  legacyEmployeeTypeDisabled,
  filterFacetOptions,
  facetPolicy = POLICYTYPE.RESOURCE_MANAGEMENT,
  scope
}) => {
  const includeProjectManagerDataAccessScope =
    facetPolicy === POLICYTYPE.PROJECT_MANAGEMENT;

  return [
    ...[
      includeProjectManagerDataAccessScope
        ? ProjectResourceFacet
        : ResourceFacet
    ],
    ...(filterFacetOptions.role &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_PROJECT_ROLE,
      includeProjectManagerDataAccessScope
    })
      ? [primaryRoleFacet, RoleFacet]
      : []),
    ...(filterFacetOptions.project
      ? [
          includeProjectManagerDataAccessScope
            ? EnhancedPMProjectFacet
            : ProjectFacet
        ]
      : []),
    ...(filterFacetOptions.client
      ? [
          includeProjectManagerDataAccessScope
            ? EnhancedPMClientFacet
            : ClientFacet
        ]
      : []),
    ...(filterFacetOptions.program
      ? [
          includeProjectManagerDataAccessScope
            ? EnhancedPMProgramFacet
            : ProgramFacet
        ]
      : []),
    ...(filterFacetOptions.skill &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_SKILL_ASSIGNMENTS,
      includeProjectManagerDataAccessScope
    })
      ? [SkillFacet]
      : []),
    ...(filterFacetOptions.location &&
    groupSettings.location?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_LOCATION,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, LocationFacet, scope)]
      : []),
    ...(filterFacetOptions.division &&
    groupSettings.division?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_DIVISION,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, DivisionFacet, scope)]
      : []),
    ...(filterFacetOptions.serviceCenter &&
    groupSettings.serviceCenter?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_SERVICE_CENTER,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, ServiceCenterFacet, scope)]
      : []),
    ...(filterFacetOptions.costCenter &&
    groupSettings.costCenter?.isEnabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_COST_CENTER,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, CostCenterFacet, scope)]
      : []),
    ...(filterFacetOptions.department &&
    groupSettings.department?.isEnabled &&
    legacyDepartmentDisabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_DEPARTMENT,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, DepartmentFacet, scope)]
      : []),
    ...(filterFacetOptions.employeeType &&
    groupSettings.employeeType?.isEnabled &&
    legacyEmployeeTypeDisabled &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_EMPLOYEE_TYPE,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, EmployeeTypeFacet, scope)]
      : []),
    ...(filterFacetOptions.resourcePool &&
    permissions.some(
      p => p.permissionActionUri === USER_PERMISSION.VIEW_RESOURCE_POOL
    )
      ? [ResourcePoolFacet]
      : []),
    ...(filterFacetOptions.tags &&
    hasViewPermission({
      permissions,
      actionUri: USER_PERMISSION.VIEW_CUSTOM_FIELDS,
      includeProjectManagerDataAccessScope
    })
      ? [applyPolicyToGroupFacet(facetPolicy, TagFacet)]
      : [])
  ];
};

export default getFacets;
